import { DefaultOptionType } from 'antd/lib/select';
import { ConfigType } from 'models/ConfigType';
import { useLocation, useParams } from 'react-router-dom';
import { useGetBusinessQuery, useGetDivisionQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useGetConfigTypesQuery } from 'redux/services/animals/animalsApi';

export const CONFIG_TYPES_UNAVAILABLE_MESSAGE = 'Config Types could not be loaded from D365. Config Type cannot be set until the list loads — reload the page or contact support.';

const NO_DATA_AREA_ID_MESSAGE = 'Config Types could not be loaded because no D365 company (data area) is set for this business.';

export interface UseConfigTypesResult {
  /** The codes and descriptions the animal service returned. Empty whenever the list has not successfully loaded. */
  configTypes: ConfigType[];
  /**
   * The same rows shaped for antd's `Select`: the label is `"${configTypeId} - ${description}"`
   * (just `configTypeId`, with no trailing " - ", when the description is blank or whitespace),
   * the short code is stored as the value.
   */
  options: DefaultOptionType[];
  isLoading: boolean;
  isError: boolean;
  /** Set whenever `isError` is true; safe to show to a user. */
  errorMessage?: string;
}

/**
 * Builds the label shown for a Config Type option: `"${configTypeId} - ${description}"`, or just
 * `configTypeId` (no trailing " - ") when the description is blank or whitespace-only.
 */
export const formatConfigTypeLabel = ({ configTypeId, description }: ConfigType): string => {
  const trimmedDescription = description?.trim();

  return trimmedDescription ? `${configTypeId} - ${trimmedDescription}` : configTypeId;
};

/**
 * Supplies the Default Config Type options from the animal service (`GET /configTypes?dataAreaId=`)
 * rather than from a list compiled into beaker.
 *
 * There is deliberately no hardcoded fallback. If the lookup fails the caller gets an empty list
 * and an error message: a stale local list is exactly the drift this feature exists to remove, and
 * a code D365 no longer knows would be rejected on the sales order line.
 *
 * Config Types are per D365 company, so the lookup needs the business's `dataAreaId`. On
 * `/division/:id` it comes from the division's `business.dataAreaId`; on `/business/:id` from the
 * business itself. A route where neither resolves is reported as an error, not as an empty list.
 */
export const useConfigTypes = (): UseConfigTypesResult => {
  /* ******************** Hooks ******************** */
  const { id: entityId } = useParams();
  const { pathname } = useLocation();
  const isBusinessRoute = pathname.includes('/business/');
  const isDivisionRoute = pathname.includes('/division/');

  const { data: business, isLoading: isBusinessLoading, isError: isBusinessError } = useGetBusinessQuery(entityId as string, { skip: !entityId || !isBusinessRoute });
  const { data: division, isLoading: isDivisionLoading, isError: isDivisionError } = useGetDivisionQuery(entityId as string, { skip: !entityId || !isDivisionRoute });

  const dataAreaId = isBusinessRoute ? business?.dataAreaId : isDivisionRoute ? division?.business?.dataAreaId : undefined;

  const { data: configTypesResponse, isLoading: isConfigTypesLoading, isError: isConfigTypesError } = useGetConfigTypesQuery({ dataAreaId: dataAreaId as string }, { skip: !dataAreaId });

  /* ******************** Variables ******************** */
  const isEntityLoading = isBusinessLoading || isDivisionLoading;
  const isEntityError = isBusinessError || isDivisionError;
  const isLoading = isEntityLoading || (!!dataAreaId && isConfigTypesLoading);
  // A business with no dataAreaId, or a route with no business or division, leaves no D365 company to
  // look the list up against. That is a failure to load, not an empty list of config types.
  const hasNoDataAreaId = !dataAreaId && !isEntityLoading && !isEntityError;
  const isError = isEntityError || isConfigTypesError || hasNoDataAreaId;

  const configTypes = isError ? [] : configTypesResponse?.data ?? [];

  return {
    configTypes,
    options: configTypes.map((configType) => ({ label: formatConfigTypeLabel(configType), value: configType.configTypeId })),
    isLoading,
    isError,
    errorMessage: isError ? (hasNoDataAreaId ? NO_DATA_AREA_ID_MESSAGE : CONFIG_TYPES_UNAVAILABLE_MESSAGE) : undefined
  };
};
